import { createContext, useContext, useEffect, useRef, useState } from "react";
import { Eye, EyeOff, Loader } from "react-feather";
import PhoneInput from "react-phone-number-input";
import { useTranslation } from "react-i18next";
import { Radio, RadioGroup as MUIRadioGroup } from "@mui/material";
import "./Form.css";
import Button from "../Button/Button";
import CustomSelect from "../CustomSelect/CustomSelect";
import FileDrop from "../FileDrop/FileDrop";

const FormContext = createContext(null);

function FormProvider({ children, value }) {
  return <FormContext.Provider value={value}>{children}</FormContext.Provider>;
}

function useFormContext() {
  return useContext(FormContext);
}

export function Form(props) {
  const {
    inputs = [],
    onSubmit,
    errors = [],
    children,
    resetLoader = false,
    externalLoading = false,
  } = props;

  const [formLoading, setFormLoading] = useState(false);

  useEffect(() => {
    setFormLoading(externalLoading);
  }, [externalLoading]);

  useEffect(() => {
    if (errors.length > 0 || resetLoader) {
      setFormLoading(false);
    }
  }, [errors, resetLoader]);

  return (
    <FormProvider value={{ formLoading, setFormLoading, ...props }}>
      <FormComponent>
        {errors.length > 0 && <FormErrors />}

        {/* inputs can be either children or props */}
        {inputs.length > 0 && (
          <Inputs>
            {inputs.map((inputGroup, index) => (
              <Inputs
                grid={inputGroup.display === "grid"}
                gridTemplateColumns={inputGroup.gridTemplateColumns}
                key={index}
              >
                {inputGroup.data.map((input, index) => (
                  <Input key={index} {...input} />
                ))}
              </Inputs>
            ))}
          </Inputs>
        )}
        {children && <Inputs>{children}</Inputs>}

        {onSubmit && <FormButton />}
      </FormComponent>
    </FormProvider>
  );
}

function FormComponent({ children }) {
  const { formLoading, className, style, setFormLoading, onSubmit } =
    useFormContext();

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormLoading(true);
    onSubmit();
  };

  return (
    <fieldset disabled={formLoading}>
      <form
        className={["Form", className].filter(Boolean).join(" ")}
        required
        onSubmit={handleSubmit}
        style={style}
      >
        {children}
      </form>
    </fieldset>
  );
}

function FormButton() {
  const { t } = useTranslation();

  const {
    button,
    onSubmit,
    isButtonDisabled = false,
    buttonVariant = "primary_a",
    formLoading,
  } = useFormContext();

  return (
    <Button
      text={
        formLoading
          ? t("common:loading")
          : onSubmit && !button
            ? t("common:confirm")
            : button
      }
      variant={buttonVariant}
      className="Form-button"
      type="submit"
      disabled={formLoading || isButtonDisabled}
    />
  );
}

function FormErrors() {
  const { errors } = useFormContext();

  return (
    <ul className="Form-errors">
      {errors.map((error, index) => (
        <li key={index}>{error}</li>
      ))}
    </ul>
  );
}

export function useFormErrors(inputKeys = [], formKey = "non_field_errors") {
  const initialErrors = Object.fromEntries(
    [...inputKeys, formKey].map((key) => [key, []]),
  );

  const [errors, setErrors] = useState(initialErrors);

  const clearErrors = () => {
    setErrors(initialErrors);
  };

  return { errors, setErrors, clearErrors };
}

export const FormLoader = ({ size }) => {
  return (
    <div className="Form-button-loaderIcon rotate">
      <Loader size={size} />
    </div>
  );
};

export function Inputs({ children, grid, gridTemplateColumns }) {
  return (
    <div
      style={grid ? { gridTemplateColumns } : {}}
      className={["Inputs", grid && "grid"].filter(Boolean).join(" ")}
    >
      {children}
    </div>
  );
}

const InputContext = createContext(null);

function InputProvider({ children, value }) {
  return (
    <InputContext.Provider value={value}>{children}</InputContext.Provider>
  );
}

export function useInputContext() {
  return useContext(InputContext);
}

export function Input(props) {
  const {
    label,
    type,
    errors = [],
    className,
    rowRadio,
    style,
    unstyled = false,
    required = true,
    floatingLabel,
    value,
  } = props;

  const components = {
    file: <FileDrop />,
    number: <NumberInput />,
    select: <CustomSelect />,
    radio: <RadioGroup />,
    password: <PasswordInput />,
    tel: <PhoneInputWrapper />,
    checkbox: <CheckboxInput />,
  };

  const [focused, setFocused] = useState(false);
  const hasValue =
    value !== undefined &&
    value !== null &&
    value !== "" &&
    // keep floating label visible for falsy-but-meaningful values (e.g. 0)
    !(typeof value === "number" && Number.isNaN(value));
  const floatingLabelProps =
    type !== "checkbox"
      ? {
          title: label,
          className: type !== "checkbox" ? "InputFloatingLabel" : "",
          style: { visibility: focused || hasValue ? "visible" : "hidden" },
        }
      : {};

  return (
    <InputProvider
      value={{
        ...props,
        required: required,
        placeholder: floatingLabel
          ? focused
            ? ""
            : props.label
          : props.placeholder,
        label: floatingLabel ? "" : props.label,
      }}
    >
      <div
        style={style}
        className={[
          "Input",
          className,
          type === "checkbox" && "CheckboxInput",
          rowRadio && "rowRadio",
          unstyled && "unstyled",
        ]
          .filter(Boolean)
          .join(" ")}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
      >
        <InputHugger>
          {floatingLabel && <div {...floatingLabelProps}>{label}</div>}
          {label && !floatingLabel && <span>{label}</span>}
          {components[type] || <DefaultInput />}
        </InputHugger>

        {errors.length > 0 && <InputErrors />}
      </div>
    </InputProvider>
  );
}

function InputErrors() {
  const { errors } = useInputContext();

  // Update parent form loading state based on child input errors
  const { setFormLoading } = useFormContext();
  useEffect(() => {
    if (setFormLoading && errors.length > 0) {
      setFormLoading(false);
    }
  }, [errors]);

  return (
    <ul className="Input-errors">
      {errors.map((error, index) => (
        <li key={index}>{error}</li>
      ))}
    </ul>
  );
}

function InputHugger({ children }) {
  const { type } = useInputContext();
  if (type === "file") return <div>{children}</div>;
  return <label>{children}</label>;
}

function DefaultInput() {
  const {
    required,
    type,
    name,
    autoComplete,
    value,
    onChange,
    disabled,
    placeholder,
    minLength,
    maxLength,
    min,
    max,
    step,
    pattern,
  } = useInputContext();

  const safeValue = value ?? "";

  return (
    <input
      required={required}
      type={type}
      name={name}
      id={name}
      autoComplete={autoComplete}
      value={safeValue}
      onChange={({ target: { value } }) => onChange(value)}
      disabled={disabled}
      placeholder={placeholder}
      minLength={minLength}
      maxLength={maxLength}
      min={min}
      max={max}
      step={step}
      pattern={pattern}
    />
  );
}

function NumberInput() {
  const {
    onFormatNumber,
    required,
    name,
    autoComplete,
    value,
    onChange,
    disabled,
    placeholder,
    minLength,
    maxLength,
    min,
    max,
    step,
    pattern,
  } = useInputContext();

  const { t } = useTranslation(["common", "errors"]);
  const ref = useRef(null);

  const rawValue = value ?? "";
  const formatNumber = (v) => {
    if (!onFormatNumber) return v;
    if (v === "" || v === null || v === undefined) return "";
    return onFormatNumber(v);
  };

  const [formattedValue, setFormattedValue] = useState(formatNumber(rawValue));
  const [focused, setFocused] = useState(false);
  const handleFocus = () => setFocused(true);
  const handleBlur = () => {
    const validateNumber = (value) => {
      ref.current.setCustomValidity("");

      if (value === "" || value === null || value === undefined) return;

      if (value < min) {
        ref.current.setCustomValidity(t("errors:inputs.min", { min: min }));
      }

      if (value > max) {
        ref.current.setCustomValidity(t("errors:inputs.max", { min: max }));
      }
    };

    if (onFormatNumber) validateNumber(rawValue);
    setFocused(false);
  };

  const handleNumberChange = ({ target: { value } }) => {
    // raw value is conserved
    onChange(value);
    // formatted value is updated
    if (onFormatNumber) setFormattedValue(formatNumber(value));
  };

  // formatted value is also updated in case of onFormatNumber fn change (example: currency select change)
  useEffect(() => {
    if (onFormatNumber) setFormattedValue(formatNumber(rawValue));
  }, [onFormatNumber]);

  const inputProps = {
    type: onFormatNumber ? (focused ? "number" : "text") : "number",
    ref,
    value: onFormatNumber ? (focused ? rawValue : formattedValue) : rawValue,
    onFocus: handleFocus,
    onBlur: handleBlur,
    onChange: handleNumberChange,
  };

  return (
    <input
      required={required}
      name={name}
      id={name}
      autoComplete={autoComplete}
      disabled={disabled}
      placeholder={placeholder}
      minLength={minLength}
      maxLength={maxLength}
      min={min}
      max={max}
      step={step}
      pattern={pattern}
      {...inputProps}
    />
  );
}

function CheckboxInput() {
  const { required, disabled, value, onChange, name } = useInputContext();

  const handleCheck = ({ target: { checked } }) => onChange(checked);

  const inputProps = {
    type: "checkbox",
    checked: Boolean(value),
    required,
    disabled,
    onChange: handleCheck,
    name: name,
    id: name,
  };
  return <input {...inputProps} />;
}

function PhoneInputWrapper() {
  const {
    name,
    value,
    onChange,
    required,
    minLength = 4,
    placeholder,
  } = useInputContext();
  const { t } = useTranslation(["common", "errors"]);
  const inputRef = useRef(null);

  const [countryCode, setCountryCode] = useState("FR");
  const [showCountry, setShowCountry] = useState(false);

  const getCountry = async () => {
    const token = "8c21ef48294b96";
    const response = await fetch("https://ipinfo.io/json", {
      referrerPolicy: "origin-when-cross-origin",
      headers: { Authorization: `Bearer ${token}` },
    });
    if (response.ok) {
      const jsonResponse = await response.json();
      setCountryCode(jsonResponse.country);
    }
  };

  const handleFocus = () => {
    if (import.meta.env.VITE_ENV !== "development") {
      getCountry();
    }
    setShowCountry(true);
  };

  const validate = (value) => {
    inputRef.current.setCustomValidity("");
    if (value.length < minLength) {
      inputRef.current.setCustomValidity(t("errors:inputs.empty"));
    }
  };

  const handleBlur = () => {
    if (value) {
      validate(value);
    }
    if (!value) setShowCountry(false);
  };

  return (
    <PhoneInput
      ref={inputRef}
      defaultCountry={showCountry ? countryCode : undefined} // show only if focused or has value
      value={value ?? undefined}
      onChange={onChange}
      international
      name={name}
      required={required}
      placeholder={placeholder}
      id={name}
      onFocus={handleFocus}
      onBlur={handleBlur}
    />
  );
}

// Special password component with password toggle logic
function PasswordInput() {
  const { autoComplete, value, onChange, name, required, placeholder } =
    useInputContext();
  const [shownPassword, setShownPassword] = useState(false);
  const togglePassword = () => setShownPassword(!shownPassword);

  return (
    <div className="PasswordInput">
      <input
        className="PasswordInputInput"
        name={name}
        id={name}
        required={required}
        type={shownPassword ? "text" : "password"}
        autoComplete={autoComplete}
        value={value ?? ""}
        onChange={({ target: { value } }) => onChange(value)}
        placeholder={placeholder}
      />
      <div onClick={togglePassword} className="PasswordInputToggle">
        {shownPassword ? <EyeOff /> : <Eye />}
      </div>
    </div>
  );
}

function RadioGroup() {
  const {
    name,
    required,
    options,
    value = null,
    onChange,
    checkedIcon,
  } = useInputContext();
  const [radioValue, setRadioValue] = useState(
    value ? options.find((option) => option.value === value.value).value : "",
  );

  const handleRadioValueChange = ({ target: { value } }) => {
    const localValue = value.toString();
    setRadioValue(localValue);
    const selectedOption = options.find(
      (option) => option.value.toString() === localValue,
    );
    onChange(selectedOption);
  };

  return (
    <MUIRadioGroup
      className="RadioGroup"
      name={name}
      value={radioValue}
      onChange={handleRadioValueChange}
      required={required}
    >
      {options.map((option, index) => (
        <div key={index} className="RadioGroup-Input">
          {option.label && (
            <label>
              <Radio
                required={required}
                sx={{ padding: 0 }}
                checkedIcon={checkedIcon}
                value={option.value}
                label={option.label}
              />
              <span
                style={
                  radioValue === option.value ? { fontWeight: "500" } : null
                }
              >
                {option.label}
              </span>
            </label>
          )}
        </div>
      ))}
    </MUIRadioGroup>
  );
}
